import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../config/apiBase';

// Página de Embedded Signup do WhatsApp Cloud API (Facebook Login for Business).
// Carrega o JS SDK do FB, abre o popup de login, recebe um 'code' e manda
// pro backend pra trocar por access_token + capturar WABA_ID e Phone Number ID.
export default function WhatsAppSignupPage() {
    const [cfg, setCfg] = useState(null);
    const [status, setStatus] = useState('idle'); // idle | sdk_loading | sdk_ready | signing | exchanging | done | error
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [appId, setAppId] = useState('');
    const [appSecret, setAppSecret] = useState('');
    const [configId, setConfigId] = useState('');
    const [saving, setSaving] = useState(false);
    const sdkLoaded = useRef(false);

    useEffect(() => { fetchCfg(); }, []);

    const fetchCfg = async () => {
        try {
            const r = await fetch(`${API}/admin/master/whatsapp-app-config`, { credentials: 'include' });
            const d = await r.json();
            if (d.ok) {
                setCfg(d.config);
                setAppId(d.config.app_id || '');
                setConfigId(d.config.config_id || '');
            }
        } catch (e) {
            setError('Erro lendo config: ' + e.message);
        }
    };

    const saveAppConfig = async () => {
        setSaving(true);
        setError('');
        try {
            const body = { app_id: appId, config_id: configId };
            if (appSecret) body.app_secret = appSecret;
            const r = await fetch(`${API}/admin/master/whatsapp-app-config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
            const d = await r.json();
            if (!d.ok) throw new Error(d.detail || 'Falha ao salvar');
            await fetchCfg();
            setAppSecret('');
            setStatus('idle');
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const loadSdk = () => new Promise((resolve, reject) => {
        if (sdkLoaded.current && window.FB) return resolve();
        setStatus('sdk_loading');
        // Add SDK
        const script = document.createElement('script');
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
            try {
                window.FB.init({
                    appId,
                    cookie: true,
                    xfbml: false,
                    version: 'v21.0',
                });
                sdkLoaded.current = true;
                setStatus('sdk_ready');
                resolve();
            } catch (e) {
                reject(e);
            }
        };
        script.onerror = () => reject(new Error('Falha carregando SDK do Facebook'));
        document.body.appendChild(script);
    });

    const launchSignup = async () => {
        setError('');
        setResult(null);
        if (!appId || !configId) {
            setError('Preencha App ID e Config ID primeiro');
            return;
        }
        try {
            await loadSdk();
            setStatus('signing');
            window.FB.login(
                (response) => {
                    if (response.authResponse && response.authResponse.code) {
                        exchangeCode(response.authResponse.code);
                    } else {
                        setStatus('error');
                        setError('Login cancelado ou falhou. Status: ' + response.status);
                    }
                },
                {
                    config_id: configId,
                    response_type: 'code',
                    override_default_response_type: true,
                    extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
                }
            );
        } catch (e) {
            setStatus('error');
            setError(e.message);
        }
    };

    const exchangeCode = async (code) => {
        setStatus('exchanging');
        try {
            const r = await fetch(`${API}/admin/master/whatsapp-exchange-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ code }),
            });
            const d = await r.json();
            setResult(d);
            if (d.ok) setStatus('done');
            else { setStatus('error'); setError(JSON.stringify(d.response || d, null, 2)); }
        } catch (e) {
            setStatus('error');
            setError(e.message);
        }
    };

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text)' }}>Conectar WhatsApp (Embedded Signup)</h1>
            <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                Fluxo oficial da Meta para Cloud API + Coexistência. Mantém seu número ativo no app WhatsApp Business do celular.
            </p>

            {/* Step 1: App credentials */}
            <section className="rounded-xl p-5 mb-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h2 className="font-semibold mb-3" style={{ color: 'var(--text)' }}>1. Credenciais do App Meta</h2>
                <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
                    Pegue em <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline">developers.facebook.com/apps</a> → seu app → Configurações &gt; Básico.
                </p>
                <div className="space-y-3">
                    <label className="block">
                        <span className="block text-xs mb-1" style={{ color: 'var(--muted)' }}>App ID</span>
                        <input type="text" value={appId} onChange={e => setAppId(e.target.value)} placeholder="123456789012345"
                            className="w-full px-3 py-2 rounded-md text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    </label>
                    <label className="block">
                        <span className="block text-xs mb-1" style={{ color: 'var(--muted)' }}>App Secret {cfg?.has_app_secret ? <em className="opacity-60">(já salvo: {cfg.app_secret_masked})</em> : ''}</span>
                        <input type="password" value={appSecret} onChange={e => setAppSecret(e.target.value)} placeholder={cfg?.has_app_secret ? 'Deixe vazio pra não mudar' : 'Cole o secret aqui'}
                            className="w-full px-3 py-2 rounded-md text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    </label>
                    <label className="block">
                        <span className="block text-xs mb-1" style={{ color: 'var(--muted)' }}>Config ID (Embedded Signup)</span>
                        <input type="text" value={configId} onChange={e => setConfigId(e.target.value)} placeholder="ID da configuração de Login for Business"
                            className="w-full px-3 py-2 rounded-md text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    </label>
                    <button onClick={saveAppConfig} disabled={saving} className="px-4 py-2 rounded-md text-sm font-medium"
                        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)', opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Salvando...' : 'Salvar credenciais'}
                    </button>
                </div>
            </section>

            {/* Step 2: Launch signup */}
            <section className="rounded-xl p-5 mb-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h2 className="font-semibold mb-3" style={{ color: 'var(--text)' }}>2. Conectar WhatsApp</h2>
                <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
                    Vai abrir popup da Meta. Escolhe seu Business, o número, e ativa <strong>Coexistência</strong> quando aparecer.
                </p>
                <button onClick={launchSignup} disabled={!cfg?.has_app_secret || status === 'signing' || status === 'exchanging' || status === 'sdk_loading'}
                    className="px-5 py-2.5 rounded-md text-sm font-semibold"
                    style={{ background: '#25D366', color: '#fff', opacity: (!cfg?.has_app_secret || status === 'signing' || status === 'exchanging' || status === 'sdk_loading') ? 0.5 : 1 }}>
                    {status === 'sdk_loading' ? 'Carregando SDK...' : status === 'signing' ? 'Aguardando login...' : status === 'exchanging' ? 'Trocando código...' : 'Conectar WhatsApp via Meta'}
                </button>
                {!cfg?.has_app_secret && <p className="mt-2 text-xs" style={{ color: 'var(--muted-2)' }}>Salve App Secret primeiro.</p>}
            </section>

            {/* Result */}
            {status === 'done' && result && (
                <section className="rounded-xl p-5 mb-6" style={{ background: 'rgba(37, 211, 102, 0.08)', border: '1px solid rgba(37, 211, 102, 0.3)' }}>
                    <h2 className="font-semibold mb-3" style={{ color: '#25D366' }}>✓ Conectado!</h2>
                    <ul className="text-sm space-y-1" style={{ color: 'var(--text)' }}>
                        <li>WABA ID: <code className="opacity-80">{result.waba_id || '(não detectado)'}</code></li>
                        <li>Phone Number ID: <code className="opacity-80">{result.phone_number_id || '(não detectado)'}</code></li>
                        <li>Businesses: {(result.businesses || []).map(b => b.name).join(', ') || '—'}</li>
                    </ul>
                </section>
            )}

            {error && (
                <section className="rounded-xl p-4 mb-6" style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <h3 className="font-semibold text-sm mb-1" style={{ color: '#ef4444' }}>Erro</h3>
                    <pre className="text-xs whitespace-pre-wrap" style={{ color: 'var(--muted)' }}>{error}</pre>
                </section>
            )}
        </div>
    );
}
